import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose, setDisplayName } from 'recompose';
import {
  get,
  noop,
  compact,
} from 'lodash';
import { Element } from 'react-scroll';
import Button from '@material-ui/core/Button';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CardHeader from '@material-ui/core/CardHeader';
import Collapse from '@material-ui/core/Collapse';
import IconButton from '@material-ui/core/IconButton';
import { withStyles } from '@material-ui/core/styles';
import ExpandIcon from '@material-ui/icons/KeyboardArrowDown';
import CollapseIcon from '@material-ui/icons/KeyboardArrowUp';
import WarningIcon from '@material-ui/icons/WarningRounded';
import LocalShipping from '@material-ui/icons/LocalShipping';
import Visibility from '@material-ui/icons/Visibility';
import ShoppingCart from '@material-ui/icons/ShoppingCart';
import { green } from '@material-ui/core/colors';

import {
  formatPrice,
  getOrderStatus,
  toDefaultDateTimeFormat,
  toDefaultDateTimeFormatForBottom,
  toShortestDefaultDateFormat,
  updatedClassname,
  scrollTo,
} from 'helpers/utils';
import immutableToJS from 'helpers/immutableToJS';
import {
  Avatar,
  HighlightText,
  injectIntl,
  InvoiceBadge,
  Tooltip,
  Badge,
} from 'components';
import { markAsPaid } from 'redux/orders/actions';
import { selectState } from 'redux/selectors';
import InvoiceDetail from './InvoiceDetail';
import OrderStatusBadge from './OrderStatusBadge';
import OrderPendingStatusBadge from './OrderPendingStatusBadge';
import PayNowButton from './PayNowButton';

const styles = theme => ({
  cardRoot: {
    paddingTop: 5,
    paddingBottom: 5,
    [theme.breakpoints.down('sm')]: {
      paddingLeft: 0,
      marginLeft: '-6px',
    },
  },
  readCard: {
    backgroundColor: '#f4f4f4',
  },
  avatar: {
    boxShadow: 'rgba(0, 0, 0, 0.12) 0px 1px 6px, rgba(0, 0, 0, 0.12) 0px 1px 4px',
    marginRight: 10,
    overflow: 'hidden',
  },
  btnLabel: {
    color: '#fff',
    fontSize: 12,
    fontWeight: 400,
    [theme.breakpoints.down('md')]: {
      fontSize: 10,
    },
  },
  btnLabelPlaceOrder: {
    color: '#fff',
    fontSize: 12,
    fontWeight: 500,
    [theme.breakpoints.down('md')]: {
      fontSize: 10,
    },
  },
  buyerConfirmBtn: { backgroundColor: '#ff0000' },
  cardAction: {
    marginTop: 0,
  },
  cardTitle: {
    paddingRight: 0,
    width: '100%',
    fontWeight: 600,
  },
  readTitle: {
    fontWeight: 400,
  },
  confirmDeliveredBtn: {
    backgroundColor: '#ff0000',
    padding: 0,
    marginLeft: 0,
    marginRight: 0,
    minHeight: 'unset',
  },
  cutOffBtn: {
    backgroundColor: '#5dadff',
    padding: 0,
    marginLeft: 0,
    marginRight: 0,
    minHeight: 'unset',
  },
  element: { padding: '1px 0' },
  finalizeBtn: {
    backgroundColor: '#e25241',
    marginLeft: 0,
    marginRight: 0,
    padding: 0,
    minHeight: 'unset',
  },
  noPadding: { padding: 0 },
  orderBtn: {
    backgroundColor: '#00c308',
    padding: 0,
    marginLeft: 0,
    marginRight: 10,
    minWidth: '50px',
    minHeight: '25px',
    paddingLeft: '10px',
    paddingRight: '10px',
  },
  sellerConfirmBtn: { backgroundColor: '#43a047' },
});

class InvoiceCard extends Component {
  static propTypes = {
    classes: PropTypes.shape({}).isRequired,
    expanded: PropTypes.bool,
    index: PropTypes.number.isRequired,
    order: PropTypes.shape({
      id: PropTypes.number,
      updatedAt: PropTypes.string,
    }).isRequired,
    orderItemRequesting: PropTypes.bool,
    orderRequesting: PropTypes.bool,
    searchText: PropTypes.string,

    formatMessage: PropTypes.func.isRequired,
    readOrder: PropTypes.func.isRequired,
    markAsPaid: PropTypes.func.isRequired,
    setExpanded: PropTypes.func,
    updateOrder: PropTypes.func.isRequired,
    onUpdate: PropTypes.func.isRequired,
  };

  static defaultProps = {
    expanded: false,
    orderItemRequesting: false,
    orderRequesting: false,
    searchText: '',

    setExpanded: noop,
  };

  static contextTypes = {
    currentUser: PropTypes.shape({}),
    isDistributor: PropTypes.bool,
    isFarmer: PropTypes.bool,
    isRestaurant: PropTypes.bool,
    noAutoPay: PropTypes.bool,
    payOnline: PropTypes.bool,
  };

  componentDidMount(){
    if (this.props.expanded){
      setTimeout(() => {
        this.readOrder();
        scrollTo(`invoice-${this.props.index}`);
      }, 2000);
    }
  }

  handleExpandChange = () => {
    const expanded = !this.props.expanded;
    const { index, order, setExpanded } = this.props;
    if (expanded) {
      this.readOrder();
    }
    setExpanded(expanded ? order.id : -1, () => {
      if (!expanded) return;
      setTimeout(() => scrollTo(`invoice-${index}`), 500);
    });
  };

  authorizeOrder = (e) => {
    e.stopPropagation();
    const { order, updateOrder } = this.props;
    updateOrder(order.id, { authorized: true });
  }

  buyerConfirmOrder = (e) => {
    e.stopPropagation();
    const { order, updateOrder } = this.props;
    updateOrder(order.id, { buyerConfirmed: true });
  }

  confirmDeliveryOrder = (payMethod, onSuccess) => {
    const { order, updateOrder } = this.props;
    updateOrder(order.id, { delivered: true, payMethod, onSuccess });
  }

  confirmOrder = (e) => {
    e.stopPropagation();
    const { order, updateOrder } = this.props;
    updateOrder(order.id, { confirmed: true });
  }

  placeOrder = (e) => {
    e.stopPropagation();
    const { order, updateOrder } = this.props;
    updateOrder(order.id, { isDraft: false });
  }

  readOrder = (orderId = null) => {
    const { order, readOrder, onUpdate } = this.props;
    if (orderId) {
      onUpdate(orderId);
      readOrder(orderId, { noDetail: orderId !== order.id, isTrack: true });
    } else {
      readOrder(order.id, { isTrack: true });
    }
  }

  render() {
    const {
      classes,
      index,
      order,
      orderItemRequesting,
      orderRequesting,
      searchText,
      formatMessage,
      expanded,
      updateOrder,
      markAsPaid,
    } = this.props;
    const {
      currentUser,
      isDistributor,
      isFarmer,
      isRestaurant,
      noAutoPay,
      payOnline,
    } = this.context;
    const orderStatus = getOrderStatus(order);
    const isContactInfo = !!order.contact;
    const account = order.type === 'PO' ? get(order, 'contact.sellerProfile') || get(order, 'seller.account') : get(order, 'contact.buyerProfile') || get(order, 'buyer.account');
    const logoUrl = get(account, 'logoUrl');
    const companyCode = order.type === 'SO' ? get(order, 'contact.buyerCompanyCode') : get(order, 'contact.sellerCompanyCode');
    const dateTimeClass = `invoice-date-time ${order.editorId === currentUser.id || order.editorMemberId === currentUser.id ? '' : 'updated'}`;
    const tooltip = {
      SO: formatMessage('Sales Order'),
      PO: formatMessage('Purchase Order'),
    }[order.type];
    const isDeleted = get(order, 'contact.isDeleted') || get(order, 'contact.sellerProfile.isOff');
    const { totalPrice, totalCost, feeTotal } = order;
    const netValue = totalPrice - totalCost - (totalPrice * 0.01 * feeTotal);

    const invoicePane = (
      <div style={{ alignItems: 'center' }} className="d-flex payment-span col-md-3">
        <span className="grey">
          {formatMessage('Invoice')}
        </span>&nbsp;#
        <HighlightText
          highlight={searchText}
          text={`${order.orderNum}`}
        />
      </div>
    );
    const netPane = !(order.type === 'PO') && !(isDistributor || isFarmer) && (
      <div
        style={{ alignItems: 'center' }}
        className={`d-flex payment-span col-md-3 ${updatedClassname(this, 'netValue', netValue)}`}
      >
        <span className="grey">{formatMessage('Net')}</span>
        <span className={`bold ${netValue < 0 ? 'red' : 'green'}`}>&nbsp;${formatPrice(netValue)}
        </span>
      </div>
    );
    const readBadge = !!order.sellerOpenAt && order.type === 'PO' && (
      <Tooltip
        placement="bottom-start"
        tooltip={toDefaultDateTimeFormat(order.sellerOpenAt)}
      >
        <span>
          <InvoiceBadge type="viewed">
            <Visibility style={{
              color: 'rgba(0, 0, 0, 0.5)',
              }} />
            &nbsp;
            {formatMessage('Read')}
          </InvoiceBadge>
        </span>
      </Tooltip>
    );
    const isAggregatedOrder = order.createdAsDraft;
    const deliveryFeeBadge = order.deliveryFee > 0 && (
      <InvoiceBadge type="deliveryFee" style={{ color: green[400] }}>
        <LocalShipping />
        &nbsp;
        {formatMessage('fee')}&nbsp;
        <span className="number" >
          ${order.deliveryFee}
        </span>
      </InvoiceBadge>
    );
    const discountBadge = order.contact.defaultDiscount > 0 && (
      <div className="discount-info">
        <Tooltip tooltip={formatMessage('Discount')}>
          <Badge type="discount">
            -{order.contact.defaultDiscount}&nbsp;%
          </Badge>
        </Tooltip>
      </div>
    );

    const badges = (
      <Fragment>
        <div className="d-flex" style={{ alignItems: 'center' }}>
          {order.createdAsDraft && !order.isDraft && payOnline && (
            <OrderPendingStatusBadge
              formatMessage={formatMessage}
              info={order}
            />
          )}
        </div>
        <div className="d-md-down-none">
          {readBadge}
        </div>
        <div className="first-part d-flex align-items-end">
          {(isFarmer && order.mismatch && !order.isDraft && order.type === 'SO') && (
            <div className="d-flex" style={{ justifyContent: 'flex-end', alignItems: 'center' }}>
              <span className="d-md-down-none" style={{ color: 'red', alignItems: 'center', fontWeight: '500', marginRight: '10px' }}>{formatMessage('Edit Qty')}</span>
              <Tooltip
                placement="bottom-start"
                tooltip={formatMessage('Order quantity mismatching or farm order is not placed or there was an error capturing this order.')}
              >
                <WarningIcon style={{ color: 'red', width: 40, height: 40 }} />
              </Tooltip>
            </div>
          )}
          {order.type !== 'SO' && isDistributor && (
            <div className="d-md-up">
              {order.mismatch && !order.isDraft &&(
                <div className="d-flex" style={{ justifyContent: 'flex-end', alignItems: 'center' }}>
                  <span className="d-md-down-none" style={{ color: 'red', fontWeight: '500', marginRight: '10px' }}>{formatMessage('Edit Qty')}</span>
                  <WarningIcon style={{ color: 'red', width: 40, height: 40 }} />
                </div>
              )}
              {/* {order.isDraft && (
                <div className="d-flex" style={{ justifyContent: 'flex-end', alignItems: 'center' }}>
                  <span className="d-md-down-none" style={{ color: 'red', fontWeight: '500', marginRight: '10px' }}>{formatMessage('Send P.O.')}</span>
                  <WarningIcon style={{ color: 'red', width: 40, height: 40 }} />
                </div>
              )} */}
            </div>
          )}
        </div>
        <div className="d-md-down-none">
          {discountBadge}
        </div>
        <div className="d-md-down-none">
          {deliveryFeeBadge}
        </div>
       
      </Fragment>
    );
    const subTitle = (
      <div className="sub-title col-md-9 d-md-down-none">
        <div className="row" style={{ width: '100%' }}>
          <div className="payment-info col-md-7">
            <div className="row w-100">
              <div style={{ alignItems: 'center' }} className="payment-span d-flex col-md-3">
                <span className="grey">{formatMessage('ID')}</span>&nbsp;
                <span className="blue">
                  <HighlightText
                    highlight={searchText}
                    text={`${order.id}`}
                  />
                </span>
              </div>
              {(isDistributor || isFarmer) ? invoicePane : netPane}
 
              {order.type !== 'SO' && order.isDraft && isDistributor && (
                <div style={{ justifyContent: 'center', alignItems: 'center' }} className="col-md-4 pr-0 d-flex">
                  {/* {order.isDraft && order.mismatch && (
                    <WarningIcon
                      style={{
                        color: 'red',
                        marginRight: 10,
                        width: 40,
                        height: 40,
                      }}
                    />
                  )} */}
                  <InvoiceBadge type="draft">
                    <ShoppingCart style={{ color: 'rgba(0, 0, 0, 0.5)', marginRight:'10px' }} />
                  </InvoiceBadge>
                  <Button
                    classes={{
                      label: classes.btnLabelPlaceOrder,
                      root: classes.orderBtn,
                    }}
                    className="button"
                    disabled={orderRequesting || orderItemRequesting || order.mismatch}
                    onClick={this.placeOrder}
                    variant="contained"
                  >
                    {formatMessage('Place Order')}
                  </Button>
                 
                </div>
              )}
              {order.type === 'PO' && !order.isDraft && isDistributor && isAggregatedOrder && (
                <div style={{ justifyContent: 'center', alignItems: 'center' }} className="col-md-4 pr-0 d-flex">
                  <InvoiceBadge type="activated">
                    {formatMessage('Order Sent')}
                  </InvoiceBadge>
                </div>
              )}
              <div style={{ alignItems: 'center' }} className={`payment-span d-flex col-md-2 ${updatedClassname(this, 'totalPrice', totalPrice)}`}>
                <span className="grey">
                </span>&nbsp;<span className="font-weight-500">${formatPrice(totalPrice)}</span>
              </div>
              {order.type !== 'SO' && !isRestaurant && !isDeleted && !order.createdAsDraft &&
                !order.confirmed && isAggregatedOrder && (
                  <Button
                    classes={{
                      label: classes.btnLabel,
                      root: classes.cutOffBtn,
                    }}
                    className="confirm-button col-md-3"
                    disabled={orderRequesting || orderItemRequesting}
                    onClick={this.confirmOrder}
                    variant="contained"
                  >
                    {formatMessage('CUT-OFF')}
                  </Button>
              )}
              {/* {!noAutoPay && netValue > 0 && !isDeleted && !order.createdAsDraft && order.type === 'PO' &&
                !order.authorized && order.manualPay && (
                  <PayNowButton
                    info={order}
                    formatMessage={formatMessage}
                    confirmDelivery={this.confirmDeliveryOrder}
                  />
              )} */}
              {/* <div className="payment-span col-md-3" /> */}
              {/* {!(order.type === 'PO') &&
                <div className="payment-span left-margin">
                  <span className="grey">{formatMessage('Fee')}</span>
                  &nbsp;
                  (${formatPrice(totalPrice * 0.01 * feeTotal)})
                </div>}
              {!isFarmer && !(order.type === 'PO') && (
                <div className="payment-span left-margin">
                  <span className="grey">
                    {formatMessage('Cost')}
                  </span>&nbsp;${formatPrice(totalCost)}
                </div>
              )} */}
              {(isDistributor || isFarmer) ? netPane : invoicePane}
            </div>
          </div>
          <div className="invoice-sub-header col-md-5">
            <div className="row w-100">
              <div className="invoice-sub-header-first-part col-md-6 pl-0 pr-0">
                {order.type !== 'SO' && order.isDraft && isDistributor && (
                  <div>
                    {order.isDraft && order.mismatch && (
                      <div className="d-flex" style={{ justifyContent: 'flex-end', alignItems: 'center' }}>
                        <span className="d-md-down-none" style={{ color: 'red', fontWeight: '500', marginRight: '10px' }}>{formatMessage('Edit Qty')}</span>
                        <WarningIcon style={{ color: 'red', marginRight: 10, width: 40, height: 40 }} />
                      </div>
                    )}
                  </div>
                )}
                <span className="badges">
                  {badges}
                </span>
              </div>
              <div className="invoice-sub-header-second-part col-md-6 pl-0 pr-0">
                <span className="d-md-up-none">
                  {badges}
                </span>
                <span
                  style={{
                    color: order.mergedAt && order.type ==='SO' && isDistributor ? '#e8352f': '#4196f2',
                    fontSize: '.85rem',
                    fontWeight: 500,
                  }}
                  className={dateTimeClass}>
                  <HighlightText
                    highlight={searchText}
                    id="updatetime"
                    text={ order.mergedAt ? toDefaultDateTimeFormat(order.mergedAt) :toDefaultDateTimeFormat(order.placedAt || order.createdAt)}
                  />
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );

    return (
      <Element
        className={classes.element}
        name={`invoice-${index}`}
      >
        <Card>
          <CardHeader
            action={
              <IconButton
                aria-expanded={expanded}
                aria-label="Show more"
                onClick={this.handleExpandChange}
                style={{color:'#448fe4'}}
              >
                {expanded ? <CollapseIcon /> : <ExpandIcon />}
              </IconButton>
            }
            avatar={
              <span className="d-md-down-none">
                <Avatar
                  className={classes.avatar}
                  fullName={isContactInfo ? get(account, 'companyName') : get(account, 'profile.companyName')}
                  src={logoUrl}
                  type={order.type}
                  size={40}
                />
              </span>
            }
            classes={{
              root: compact([
                classes.cardRoot,
                !order.isDraft && !expanded ?
                order.type === 'SO' ? (
                  order.sellerOpenAt && classes.readCard
                ) : (
                  order.buyerOpenAt && classes.readCard
                ) : null,
              ]).join(' '),
              action: classes.cardAction,
              title: compact([
                classes.cardTitle,
                !order.isDraft ?
                order.type === 'SO' ? (
                  order.sellerOpenAt && classes.readTitle
                ) : (
                  order.buyerOpenAt && classes.readTitle
                ) : null,
              ]).join(' '),
            }}
            title={
              <div className="container-fluid pl-0" style={{ paddingRight: '10px' }}>
                <div className="d-lg-up-none">
                  <div className="d-flex" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                    <div className="d-flex" style={{ justifyContent: 'flex-start', alignItems: 'center' }}>
                      {/* Badge */}
                      <InvoiceBadge style={{ marginBottom: (!isRestaurant && order.mismatch && !order.isDraft) || (!order.createdAsDraft && order.type === 'PO' && order.captureFailed) ? '5px' : '' }} type={isAggregatedOrder ? (isDistributor ? 'PO' : 'AO') : order.type}>
                        <HighlightText
                          highlight={searchText}
                          id="type"
                          text={isAggregatedOrder ? (isDistributor ? 'P.O.' : 'S.O. distrib') : order.type}
                        />
                      </InvoiceBadge>

                      {/* Title */}
                      <div className="title-mobile">
                        <div className="title-content">
                          {companyCode && (
                            <span style={{ marginRight: 5, color: 'red' }}>{companyCode}</span>
                          )}
                          <HighlightText
                            highlight={searchText}
                            text={isContactInfo ? get(account, 'companyName') || '--' : get(account, 'profile.companyName') || '--'}
                          />
                        </div>
                      </div>
                    </div>
                    
                    {/* Date */}
                    <HighlightText
                      highlight={searchText}
                      id="createtime"
                      text={toShortestDefaultDateFormat(order.createdAt)}
                    />
                  </div>
                  <div className="d-flex" style={{ justifyContent: 'center', alignItems: 'center' }}>
                    {/* More badges and buttons */}
                    {order.type !== 'SO' && order.isDraft && isDistributor && (
                      <div className="d-flex" style={{ justifyContent: 'center', alignItems: 'center' }}>
                        <InvoiceBadge type="draft">
                          <ShoppingCart style={{ color: 'rgba(0, 0, 0, 0.5)' }} />
                        </InvoiceBadge>
                        <Button
                          classes={{
                            label: classes.btnLabelPlaceOrder,
                            root: classes.orderBtn,
                          }}
                          className="button"
                          disabled={orderRequesting || orderItemRequesting || order.mismatch}
                          onClick={this.placeOrder}
                          variant="contained"
                        >
                          {formatMessage('Place Order')}
                        </Button>  
                      </div>
                    )}
                    {order.type === 'PO' && !order.isDraft && isDistributor && (
                      <div className="d-flex" style={{ justifyContent: 'center', alignItems: 'center' }}>
                        <InvoiceBadge type="activated">
                          {formatMessage('Order Sent')}
                        </InvoiceBadge>
                      </div>
                    )}
                    {badges}
                  </div>
                </div>

                <div className="d-md-down-none">
                  <div className="row">
                    {/* Title */}
                    <div className="title col-md-2 pl-0">
                      <div className="title-content">
                        {companyCode && (
                          <span style={{ marginRight: 5, color: 'red' }}>{companyCode}</span>
                        )}
                        <HighlightText
                          highlight={searchText}
                          text={isContactInfo ? get(account, 'companyName') || '--' : get(account, 'profile.companyName') || '--'}
                        />
                      </div>
                    </div>
                    
                    {/* Badge */}
                    <span
                      style={{
                        alignSelf: 'center',
                      }}
                      className="d-md-down-none col-md-1">
                      <InvoiceBadge type={isAggregatedOrder ? (isDistributor ? 'PO' : 'AO') : (order.type === 'SO' ? !order.isConsumer ? 'SO' : 'CSO' : isDistributor ? 'POD' : order.type)}>
                        {/* Note: POD = PO Direct */}
                        <HighlightText
                          highlight={searchText}
                          id="type"
                          text={isAggregatedOrder ? (isDistributor ? 'P.O.' : 'S.O. distrib') : (order.type === 'SO' ? !order.isConsumer ? 'S.O.' : 'S.O. Retail' : isDistributor ? 'P.O.*' : 'P.O.')}
                        />
                      </InvoiceBadge>
                    </span>
                    <div className="d-lg-up-none title-mobile-content col-md-9 d-flex align-items-end">
                      <InvoiceBadge style={{ marginBottom: (!isRestaurant && order.mismatch && !order.isDraft) || (!order.createdAsDraft && order.type === 'PO' && order.captureFailed) ? '5px' : '' }} type={isAggregatedOrder ? (isDistributor ? 'PO' : 'AO') : order.type}>
                        <HighlightText
                          highlight={searchText}
                          id="type"
                          text={isAggregatedOrder ? (isDistributor ? 'P.O.' : 'S.O. distrib') : order.type}
                        />
                      </InvoiceBadge>
                      {order.type !== 'SO' && order.isDraft && isDistributor && (
                        <div className="col-md-4 pr-0 d-flex" style={{ justifyContent: 'flex-end', alignItems: 'center' }}>
                          <Button
                            classes={{
                              label: classes.btnLabel,
                              root: classes.orderBtn,
                            }}
                            className="button"
                            disabled={orderRequesting || orderItemRequesting || order.mismatch}
                            onClick={this.placeOrder}
                            variant="contained"
                          >
                            {formatMessage('Order')}
                          </Button>
                          <InvoiceBadge type="draft">
                            <ShoppingCart style={{ color: 'rgba(0, 0, 0, 0.5)' }} />
                          </InvoiceBadge>
                        </div>
                      )}
                      <div style={{ justifyContent: 'flex-end' }} className="d-flex first-part col-md-7 offset-1 pr-0 pl-0">
                        {badges}
                      </div>
                    </div>
                    {subTitle}
                  </div>
                </div>
              </div>
            }
          />
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <CardContent
              className={`${orderRequesting || orderItemRequesting ? 'loading' : ''} loading-enabled ${classes.noPadding}`}
            >
              <InvoiceDetail
                isTrackOrderPage
                formatMessage={formatMessage}
                order={order}
                orderRequesting={orderItemRequesting}
                readOrder={this.readOrder}
                updateOrder={updateOrder}
              />
            </CardContent>
          </Collapse>
        </Card>
      </Element>
    );
  }
}

/* istanbul ignore next */
const mapStateToProps = state => ({
  ...selectState('orderItems', 'orderItem')(state, 'orderItem'),
});

export default compose(
  setDisplayName('InvoiceCard'),
  connect(
    mapStateToProps,
    dispatch => ({
      markAsPaid: (...args) => dispatch(markAsPaid(...args)),
    }),
  ),
  withStyles(styles),
  immutableToJS,
  injectIntl,
)(InvoiceCard);
